'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

/**
 * Partners we actually work with, with the outbound link on the name.
 *
 * Only companies there is a real working relationship with belong here. A
 * partner page is read as a claim about a commercial relationship, so an
 * aspirational entry is a liability rather than a marketing win.
 */
const partners = [
  {
    tag: '[01]',
    name: 'Netru',
    url: 'https://netru.io',
    type: 'Compliance and certification',
    desc: 'Netru runs compliance programmes end to end. SOC 2, ISO 27001, ISO 42001, GDPR, HIPAA and Cyber Essentials, including the audit window itself.',
    fit: 'We build and run the security engineering. Netru takes the same controls through certification and keeps the evidence current after it. Between us a client gets the thing working and the thing signed off, without two vendors arguing about which findings count.',
  },
];

export default function PartnersPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '700px', height: '700px', top: '-200px', left: '-150px', opacity: 0.25 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Partners</span>
          </div>
          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Partners]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Who we work with.
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              We do not try to be everything. Where the work runs past security engineering, we bring in people we already work with rather than stretch to cover it.
            </p>
          </div>
        </div>
      </section>

      {/* Partner list */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 gap-px bg-[#222]">
            {partners?.map((p) => (
              <div key={p?.name} className="bg-[#050a0f] p-8 lg:p-12 fade-in-up">
                <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 lg:gap-16 items-start">
                  <div>
                    <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{p?.tag}</p>
                    <a
                      href={p?.url}
                      target="_blank"
                      rel="noopener"
                      className="font-heading text-2xl text-[#f5f5f8] font-medium hover:text-[#80edfa] transition-colors inline-flex items-center gap-2"
                    >
                      {p?.name}
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M5 11L11 5M11 5H6M11 5V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                    <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mt-3">{p?.type}</p>
                  </div>
                  <div>
                    <p className="text-[#aaa] leading-relaxed mb-5">{p?.desc}</p>
                    <p className="text-sm text-[#666] leading-relaxed">{p?.fit}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-24 bg-[#0a0f14]">
        <div className="max-w-[680px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-[clamp(1.6rem,3.5vw,2.4rem)] text-[#f5f5f8] font-medium leading-tight mb-4">
            Think there is a fit?
          </h2>
          <p className="text-[#aaa] leading-relaxed mb-8">
            If you work somewhere adjacent to what we do and your clients keep asking you for the other half, tell us.
          </p>
          <Link href="/contact" className="btn-primary">Book a call</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
